import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, Search, Heart, MessageSquare, User } from 'lucide-react';
import { cn } from '@/lib/utils';
import { isNativePlatform, hapticImpact } from '@/lib/capacitor';
import { useLanguage } from '@/contexts/LanguageContext';

interface NavItem {
  icon: React.ElementType;
  label: string;
  labelAr: string;
  path: string;
  badge?: number;
}

export function MobileBottomNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const { language } = useLanguage();
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  // Only show on native mobile apps
  if (!isNativePlatform()) return null;

  const navItems: NavItem[] = [
    { icon: Home, label: 'Home', labelAr: 'الرئيسية', path: '/' },
    { icon: Search, label: 'Search', labelAr: 'بحث', path: '/properties-for-sale' },
    { icon: Heart, label: 'Favorites', labelAr: 'المفضلة', path: '/favorites' },
    { icon: MessageSquare, label: 'Messages', labelAr: 'الرسائل', path: '/messages' },
    { icon: User, label: 'Profile', labelAr: 'الحساب', path: '/profile' },
  ];

  // Hide nav on scroll down, show on scroll up
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsVisible(false);
      } else {
        setIsVisible(true);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  const handleNavClick = (path: string) => {
    hapticImpact('LIGHT').catch(() => {});
    navigate(path);
  };

  return (
    <div
      className={cn(
        'fixed bottom-0 left-0 right-0 z-50 transition-transform duration-300 ease-out',
        isVisible ? 'translate-y-0' : 'translate-y-full'
      )}
    >
      {/* Background with blur */}
      <div className="absolute inset-0 bg-background/95 backdrop-blur-xl border-t border-border/50" />
      
      {/* Nav items */}
      <nav className="relative flex items-center justify-around px-2 py-2 safe-area-bottom">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          const label = language === 'ar' ? item.labelAr : item.label;

          return (
            <button
              key={item.path}
              onClick={() => handleNavClick(item.path)}
              className={cn(
                'flex flex-col items-center justify-center min-w-[64px] py-2 px-3 rounded-xl transition-all duration-200',
                'active:scale-95',
                isActive
                  ? 'text-primary'
                  : 'text-muted-foreground'
              )}
            >
              <div className="relative">
                <Icon
                  className={cn(
                    'h-6 w-6 transition-all duration-200',
                    isActive && 'scale-110'
                  )}
                  strokeWidth={isActive ? 2.5 : 2}
                />
                {item.badge && item.badge > 0 && (
                  <div className="absolute -top-1 -right-1 h-4 w-4 bg-destructive text-destructive-foreground text-[10px] font-bold rounded-full flex items-center justify-center">
                    {item.badge > 9 ? '9+' : item.badge}
                  </div>
                )}
              </div>
              <span
                className={cn(
                  'text-[11px] font-medium mt-1 transition-all duration-200',
                  isActive ? 'opacity-100 scale-100' : 'opacity-70 scale-95'
                )}
              >
                {label}
              </span>
              {isActive && (
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1 h-1 bg-primary rounded-full" />
              )}
            </button>
          );
        })}
      </nav>
    </div>
  );
}

